<template>
  <div class="cashier-desktop cashier-desktop--home">
    <div class="cashier-desktop__shell cashier-desktop__shell--split">
      <aside class="cashier-desktop__overview">
        <div class="cashier-desktop__badge">
          <span class="cashier-desktop__badge-dot"></span>
          统一收银台
        </div>
        <div class="cashier-desktop__amount">¥{{ formattedAmount }}</div>
        <h1 class="cashier-desktop__title">{{ payOrderInfo.subject || '支付订单' }}</h1>
        <div class="cashier-desktop__subtitle">{{ payOrderInfo.mchName || '商户信息加载中' }}</div>

        <div class="cashier-desktop__overview-grid">
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">订单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.payOrderId || '--' }}</div>
          </div>
          <div class="cashier-desktop__meta-card">
            <div class="cashier-desktop__meta-label">商户单号</div>
            <div class="cashier-desktop__meta-value">{{ payOrderInfo.mchOrderNo || '--' }}</div>
          </div>
        </div>
      </aside>

      <section class="cashier-desktop__panel">
        <div class="cashier-desktop__section-head">
          <div>
            <h2 class="cashier-desktop__section-title">选择支付方式</h2>
          </div>
        </div>

        <div v-if="payWays.length > 0" class="cashier-desktop__method-grid">
          <button
            v-for="method in payWays"
            :key="method.methodKey"
            type="button"
            class="cashier-desktop__method-card"
            :class="{
              'is-active': selectedMethod === method.methodKey,
              'is-wechat': method.wayCode.startsWith('WX'),
              'is-alipay': method.wayCode.startsWith('ALI')
            }"
            @click="selectMethod(method)"
          >
            <div class="cashier-desktop__method-icon">
              <img v-if="method.wayCode.startsWith('WX')" src="../assets/icon/wx.svg" alt="微信支付" />
              <img v-else-if="method.wayCode.startsWith('ALI')" src="../assets/images/zfb.jpeg" alt="支付宝" />
            </div>
            <div>
              <div class="cashier-desktop__method-name">{{ method.displayName || method.wayName }}</div>
              <div class="cashier-desktop__method-tag">{{ getMethodTag(method) }}</div>
            </div>
            <div class="cashier-desktop__method-status"></div>
          </button>
        </div>

        <div v-else class="cashier-desktop__empty">暂无可用支付方式</div>

        <div class="cashier-desktop__footer">
          <button class="cashier-desktop__primary-btn" :disabled="!selectedMethod" @click="goPay">
            {{ selectedMethodMeta ? `使用${selectedMethodMeta.displayName || selectedMethodMeta.wayName}` : '请选择支付方式' }}
          </button>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { getPayOrderInfo, getPayWays } from '@/api/api'
import config from '@/config'
import wayCodeUtil from '@/utils/wayCode'

export default {
  name: 'CashierHome',
  data() {
    return {
      payOrderInfo: {},
      selectedMethod: '',
      selectedRoute: '',
      payWays: []
    }
  },
  computed: {
    formattedAmount() {
      const amount = this.payOrderInfo.amount
      if (amount === undefined || amount === null || Number.isNaN(Number(amount))) {
        return '--'
      }
      return (Number(amount) / 100).toFixed(2)
    },
    selectedMethodMeta() {
      return this.payWays.find(item => item.methodKey === this.selectedMethod) || null
    }
  },
  mounted() {
    this.loadOrderInfo()
    this.loadPayWays()
  },
  methods: {
    canRenderPayWay(wayCode) {
      return !!wayCodeUtil.getRouteNameByWayCode(wayCode)
    },

    loadOrderInfo() {
      getPayOrderInfo().then(res => {
        this.payOrderInfo = res
      }).catch(err => {
        console.error('获取订单信息失败', err)
        this.$router.push({name: config.errorPageRouteName, params: {errInfo: err.msg || '获取订单信息失败'}})
      })
    },

    loadPayWays() {
      getPayWays().then(res => {
        const allWays = Array.isArray(res) ? res : []
        this.payWays = this.buildDisplayPayWays(allWays)

        if (this.payWays.length > 0) {
          this.selectMethod(this.payWays[0])
        }
      }).catch(err => {
        console.error('获取支付方式失败', err)
        this.payWays = []
      })
    },

    buildDisplayPayWays(allWays) {
      const renderableWays = allWays.filter(way => this.canRenderPayWay(way.wayCode))

      if (wayCodeUtil.isAlipayClient()) {
        const aliJsapi = this.findPayWay(renderableWays, 'ALI_JSAPI')
        return aliJsapi ? [this.decoratePayWay(aliJsapi, {
          displayName: '支付宝内支付',
          methodTag: '支付宝App内确认'
        })] : []
      }

      if (wayCodeUtil.isMobileBrowser()) {
        const aliWap = this.findPayWay(renderableWays, 'ALI_WAP')
        if (!aliWap) {
          return []
        }

        return [
          this.decoratePayWay(aliWap, {
            methodKey: 'ALI_WAP_APP',
            displayName: '支付宝APP支付',
            methodTag: '优先打开支付宝APP',
            launchMode: 'app'
          }),
          this.decoratePayWay(aliWap, {
            methodKey: 'ALI_WAP_WAP',
            displayName: '支付宝网页支付',
            methodTag: '手机浏览器网页支付',
            launchMode: 'wap'
          })
        ]
      }

      const pcPayWays = [
        this.decoratePcPayWay(renderableWays, 'ALI_QR', '支付宝扫码支付', '支付宝扫码'),
        this.decoratePcPayWay(renderableWays, 'WX_NATIVE', '微信扫码支付', '微信扫码'),
        this.decoratePcPayWay(renderableWays, 'ALI_PC', '支付宝在线付款', '在线付款')
      ].filter(Boolean)

      return pcPayWays
    },

    findPayWay(payWays, wayCode) {
      return payWays.find(item => item.wayCode === wayCode) || null
    },

    decoratePcPayWay(payWays, wayCode, displayName, methodTag) {
      const payWay = this.findPayWay(payWays, wayCode)
      return payWay ? this.decoratePayWay(payWay, { displayName, methodTag }) : null
    },

    decoratePayWay(payWay, options = {}) {
      return Object.assign({}, payWay, {
        methodKey: options.methodKey || payWay.wayCode,
        displayName: options.displayName || payWay.wayName,
        methodTag: options.methodTag || this.getMethodTag(payWay.wayCode),
        launchMode: options.launchMode || null
      })
    },

    selectMethod(method) {
      this.selectedMethod = method.methodKey
      this.selectedRoute = wayCodeUtil.getRouteNameByWayCode(method.wayCode) || ''
    },

    getMethodTag(method) {
      if (method && typeof method === 'object') {
        return method.methodTag || this.getMethodTag(method.wayCode)
      }

      const wayCode = method
      const tags = {
        WX_NATIVE: '扫码支付',
        WX_JSAPI: '微信内支付',
        ALI_PC: '网页登录或扫码',
        ALI_JSAPI: '支付宝内支付',
        ALI_QR: '扫码支付',
        ALI_WAP: '浏览器支付',
        WX_BAR: '条码支付',
        YSF_BAR: '云闪付支付'
      }
      if (tags[wayCode]) {
        return tags[wayCode]
      }

      if (wayCode.startsWith('WX')) {
        return '微信支付'
      }

      if (wayCode.startsWith('ALI')) {
        return '支付宝支付'
      }

      if (wayCode.startsWith('YSF')) {
        return '云闪付支付'
      }

      return '立即支付'
    },

    goPay() {
      const method = this.selectedMethodMeta
      if (!method || !this.selectedRoute) return
      localStorage.setItem('selectedWayCode', method.wayCode)
      if (method.wayCode === 'ALI_WAP') {
        wayCodeUtil.setSelectedAlipayLaunchMode(method.launchMode || 'wap')
      } else {
        wayCodeUtil.clearSelectedAlipayLaunchMode()
      }
      this.$router.push({name: this.selectedRoute})
    }
  }
}
</script>

<style scoped>
@import './cashier-desktop.css';
</style>
